import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TimeAgo from 'react-timeago';
import { CopyToClipboard, Box, Gap, IconButton, Input } from '@salecycle/ui-component-library';
import { Text, BlueText, Sidebar, SidebarSection, SidebarSectionSubtitle, SidebarSectionTitle } from '../../styles';
import { useTemplatesListDispatch, useTemplatesListState } from '../../../context/UseTemplatesListContext';
import { useUserContext } from 'core/users/UserContext';
import { SupportedLanguageButtonContainer, SupportedLanguageButtonItem } from '../SelectedTemplateSidebar';
import { generatePath, useParams } from 'react-router';
import { DetermineLinkButton } from '../DetermineLinkButton';
import { getEditRoute } from '../utils/getEditRoute';
import ConfirmationModal from '@salecycle/experimental/ConfirmationModal';
import { useAddLanguage } from '../hooks/useAddLanguage';
import { RestoreTemplateButton } from 'sections/design/template-management/sections/template-archive/components/RestoreTemplateButton';
import { DeleteTemplateButton } from 'sections/design/template-management/sections/template-archive/components/DeleteTemplateButton';

export interface ArchivedTemplateSidebarProps {
  showEdit?: boolean;
  showModal: boolean;
  setShowModal(show: boolean): void;
  /** Additional content which will be added after the "Edit" section. */
  additionalContent?: ReactNode;
}

const ArchivedTemplateSidebar = ({
  showEdit,
  showModal,
  setShowModal,
  additionalContent,
}: ArchivedTemplateSidebarProps) => {
  const { t } = useTranslation(['common', 'designs', 'supportedLanguages', 'templates']);
  const dispatch = useTemplatesListDispatch();
  const { siteId } = useParams<{ siteId: string }>();
  const { selectedTemplate } = useTemplatesListState();

  const { getUser } = useUserContext();
  const [createdBy, setCreatedBy] = useState(getUser(selectedTemplate.audit.createdBy)?.name ?? '');

  const [lastUpdatedBy, setLastUpdatedBy] = useState(getUser(selectedTemplate.audit.lastUpdatedBy)?.name ?? '');
  const [implementedLanguages, setImplementedLanguages] = useState(selectedTemplate.supportedLanguages ?? []);
  const [templateName, setTemplateName] = useState(selectedTemplate.templateName);
  const [templateLanguageOptions, setTemplateLanguageOptions] = useState({ language: '', friendlyLanguageName: '' });

  useEffect(() => {
    setImplementedLanguages(selectedTemplate.supportedLanguages);
  }, [selectedTemplate.id, selectedTemplate.supportedLanguages]);

  useEffect(() => {
    setTemplateName(selectedTemplate.templateName);
    setLastUpdatedBy(getUser(selectedTemplate.audit.lastUpdatedBy)?.name ?? '');
    setCreatedBy(getUser(selectedTemplate.audit.createdBy)?.name ?? '');
  }, [selectedTemplate]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeSideBar = () => {
    dispatch({ type: 'RESET_SELECTED_TEMPLATE' });
  };

  const handleEditClick = (language: string) => {
    window.open(
      generatePath(getEditRoute(selectedTemplate), { siteId, id: selectedTemplate.id, language }) + '?newtab=1',
      '_blank',
    );
  };

  const handleLanguageClick = (language: string, friendlyLanguageName: string) => {
    setTemplateLanguageOptions({ language, friendlyLanguageName });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const addLanguage = useAddLanguage(selectedTemplate);

  const handleOkButtonClick = () => {
    closeModal();
    addLanguage(templateLanguageOptions.language);
  };

  return (
    <Sidebar>
      <SidebarSection>
        <Box verticalPositioning="center">
          <SidebarSectionTitle>{t('designs:designGallery.sidebar.summary')}</SidebarSectionTitle>
          <IconButton onClick={closeSideBar} icon="times" ariaLabel={t('designs:designGallery.sidebar.close')} />
        </Box>
        <SidebarSectionSubtitle data-testid="TemplateNameSubtitle">
          <Input
            label={t('designs:designGallery.sidebar.templateName')}
            ariaLabel={t('designs:designGallery.sidebar.templateName')}
            value={templateName}
            disabled
          />
        </SidebarSectionSubtitle>
        <SidebarSection>
          <CopyToClipboard
            label={t('designs:designGallery.sidebar.templateKey')}
            value={selectedTemplate.id}
            hint={t('designs:designGallery.sidebar.templateKeyHint')}
          />
          <Gap bottom={30} />

          <div>
            <Text>{t('designs:designGallery.sidebar.createdBy')}</Text>
            <BlueText data-testid="CreatedBy">{createdBy}</BlueText>
          </div>
          <Gap bottom={10} />
          <div>
            <Text>{t('designs:designGallery.sidebar.lastEdited')}</Text>
            <BlueText data-testid="LastUpdatedBy">{lastUpdatedBy}</BlueText> -{' '}
            <TimeAgo live={false} date={Number(selectedTemplate.audit.lastUpdatedAt)} />
          </div>
        </SidebarSection>
      </SidebarSection>
      <Gap bottom={20} />
      {showEdit && (
        <>
          <Gap bottom={20} />
          <SidebarSection data-testid="EditLanguagesSection">
            <SidebarSectionSubtitle data-testid="TemplateLanguageEditSubTitle">
              {t('common.edit')}
            </SidebarSectionSubtitle>
            <SupportedLanguageButtonContainer>
              {implementedLanguages.map(sl => {
                return (
                  <SupportedLanguageButtonItem key={sl.language}>
                    <DetermineLinkButton
                      language={sl.language}
                      handleEditClick={handleEditClick}
                      handleLanguageClicked={handleLanguageClick}
                    />
                  </SupportedLanguageButtonItem>
                );
              })}
            </SupportedLanguageButtonContainer>
          </SidebarSection>
          <RestoreTemplateButton selectedTemplate={selectedTemplate} />
          <DeleteTemplateButton selectedTemplate={selectedTemplate} />
        </>
      )}
      <Gap bottom={20} />

      {additionalContent}
      <ConfirmationModal
        cancelButtonLabel={t('common.cancel')}
        okButtonLabel={t('common.create')}
        heading={t('templates:modal.heading', { language: templateLanguageOptions.friendlyLanguageName })}
        description={t('templates:modal.description')}
        isOpen={showModal}
        onCancelButtonClicked={closeModal}
        onCloseButtonClicked={closeModal}
        onOkButtonClicked={handleOkButtonClick}
      />
    </Sidebar>
  );
};

ArchivedTemplateSidebar.defaultProps = {
  showEmbeddedOptions: false,
  showEdit: true,
};

export default ArchivedTemplateSidebar;
